// Wave UX-2.4 #20 (2026-05-24) — buyer→auction 신고 모달.
//
// Props:
//   auctionId — 신고 대상 경매 ID
//   onClose   — 닫기 콜백
//
// 동작: 카테고리 5종 + 자유 텍스트(선택, max 1000자) → POST /buyer-app/auction/:id/report
// 중복 신고(409): 안내 + 닫기. 성공: toast + 닫기.

import React, { useState, useEffect } from 'react';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import { API } from '../../utils/api';
import { useT } from '../../i18n/I18nProvider';
import { useNavigate } from 'react-router-dom';
import { showToast } from './Toast';

var CATEGORIES = ['fake_info', 'fraud_suspect', 'inappropriate_photo', 'price_manipulation', 'other'];

function ReportModal(props) {
  var t = useT();
  var navigate = useNavigate();
  var [category, setCategory] = useState('');
  var [description, setDescription] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [error, setError] = useState('');

  useEffect(function() {
    function onKey(e) { if (e.key === 'Escape') props.onClose && props.onClose(); }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [props]);

  function submit() {
    if (!category || submitting) return;
    setSubmitting(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/auction/' + props.auctionId + '/report', {
      method: 'POST',
      body: JSON.stringify({ category: category, description: description }),
    })
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json().then(function(d){ return { status: r.status, body: d }; });
      })
      .then(function(res) {
        if (res.status === 409 || (res.body && res.body.code === 'ALREADY_REPORTED')) {
          setError(t('reportModalDuplicateError'));
          return;
        }
        if (res.body && res.body.success) {
          showToast(t('reportModalSuccess'), { variant: 'success' });
          props.onClose && props.onClose();
          return;
        }
        setError(t('reportModalError'));
      })
      .catch(function(e){ if (!(e && e.message === 'unauthorized')) setError(t('reportModalError')); })
      .finally(function(){ setSubmitting(false); });
  }

  return (
    <div onClick={props.onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15,23,42,0.55)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,      }}>
      <div onClick={function(e){ e.stopPropagation(); }}
        style={{
          width: '100%', maxWidth: 480,
          background: 'white', borderRadius: 16,
          padding: '20px 22px 22px',
          boxShadow: '0 12px 40px rgba(15,23,42,0.2)',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>🚩 {t('reportModalTitle')}</div>
          <button type="button" onClick={props.onClose} aria-label="close"
            style={{ width: 30, height: 30, border: 'none', background: '#f1f5f9', borderRadius: '50%', color: '#64748b', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>×</button>
        </div>

        {/* 카테고리 5종 — 라디오 스타일 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {CATEGORIES.map(function(c) {
            var active = category === c;
            return (
              <button key={c} type="button" onClick={function(){ setCategory(c); }}
                style={{
                  textAlign: 'left',
                  padding: '11px 14px',
                  border: '1.5px solid ' + (active ? '#1B3A5C' : '#e2e8f0'),
                  borderRadius: 10,
                  background: active ? '#eff6ff' : 'white',
                  color: active ? '#1B3A5C' : '#334155',
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  cursor: 'pointer',                }}>
                {active ? '● ' : '○ '}{t('reportCategory_' + c)}
              </button>
            );
          })}
        </div>

        {/* 자유 텍스트 */}
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{t('reportModalDescriptionLabel')}</label>
        <textarea
          value={description}
          onChange={function(e){ setDescription(e.target.value.slice(0, 1000)); }}
          placeholder={t('reportModalDescriptionPlaceholder')}
          rows={4}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 12px',
            border: '1.5px solid #e2e8f0', borderRadius: 10,
            fontSize: 13, color: '#0f172a',
            resize: 'vertical', minHeight: 80, maxHeight: 200,
            outline: 'none',
          }}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{description.length} / 1000</div>

        {/* error */}
        {error && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 12 }}>
            {error}
          </div>
        )}

        {/* actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={props.onClose} disabled={submitting}
            style={{
              flex: 1, padding: '11px 0', border: '1px solid #cbd5e1', borderRadius: 10,
              background: 'white', color: '#475569', fontSize: 13, fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}>
            {t('reportModalCancel')}
          </button>
          <button type="button" onClick={submit} disabled={!category || submitting}
            style={{
              flex: 1, padding: '11px 0', border: 'none', borderRadius: 10,
              background: (!category || submitting) ? '#cbd5e1' : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
              color: 'white', fontSize: 13, fontWeight: 700,
              cursor: (!category || submitting) ? 'not-allowed' : 'pointer',            }}>
            {submitting ? '...' : t('reportModalSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReportModal;
